import { useMemo } from 'react';

export type Language = 'en' | 'ar';

/**
 * Generic translation object structure
 * Each translation set should have 'en' and 'ar' keys
 */
type TranslationsObject = {
  en: Record<string, string>;
  ar: Record<string, string>;
};

/**
 * Custom hook for handling translations and RTL support in AE (Arabic Emirates) pages
 * Detects language from URL path (ae_ar for Arabic or ae_en for English)
 * 
 * @param translations - Object containing 'en' and 'ar' translation dictionaries
 * @returns Translation function, language code, RTL flag, and text direction
 * 
 * @example
 * ```ts
 * const translations = {
 *   en: { greeting: 'Hello', farewell: 'Goodbye' },
 *   ar: { greeting: 'مرحبا', farewell: 'وداعا' }
 * };
 * 
 * function MyComponent() {
 *   const { t, language, isRTL, dir } = useAETranslation(translations);
 *   return <div dir={dir}>{t('greeting')}</div>;
 * }
 * ```
 */
export function useAETranslation<T extends TranslationsObject>(translations: T) {
  const { language, isRTL } = useMemo(() => {
    const pathname = window.location.pathname;
    const lang: Language = pathname.includes('/ae_ar/') ? 'ar' : 'en';
    
    return {
      language: lang,
      isRTL: lang === 'ar',
    };
  }, []);

  /**
   * Translation function that returns the translated string for the given key
   * Falls back to English if Arabic translation is missing
   * Falls back to the key itself if translation is not found
   */
  const t = (key: keyof T['en']): string => {
    return translations[language][key as string] || translations.en[key as string] || String(key);
  };

  return {
    /** Translation function */
    t,
    /** Current language code ('en' or 'ar') */
    language,
    /** Whether the current language is Right-to-Left */
    isRTL,
    /** Text direction for HTML dir attribute */
    dir: isRTL ? ('rtl' as const) : ('ltr' as const),
  };
}

